import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API_URL } from '../../context/AuthContext';
import { XMarkIcon } from '@heroicons/react/24/outline';

const tipoLabel = (t) => t === 'militar' ? '🎖️ Militar' : '🎓 Civil';

export default function ProfesorPanel() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [esMobile, setEsMobile] = useState(() => window.innerWidth <= 600);

  const [redaccionModal, setRedaccionModal] = useState(null);
  const [calForm, setCalForm] = useState({ nota: '', comentario: '' });
  const [calGuardando, setCalGuardando] = useState(false);
  const [calOk, setCalOk] = useState(false);

  const [dudaModal, setDudaModal] = useState(null);
  const [textoRespuesta, setTextoRespuesta] = useState('');
  const [respondiendo, setRespondiendo] = useState(false);
  const [respOk, setRespOk] = useState(false);

  useEffect(() => {
    const check = () => setEsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (!['profesor', 'admin'].includes(usuario?.rol)) { navigate('/'); return; }
    cargar();
  }, []);

  const cargar = async () => {
    try {
      const res = await axios.get(`${API_URL}/profesor/stats`);
      setStats(res.data);
    } catch {}
    finally { setCargando(false); }
  };

  const abrirRedaccion = (r) => {
    setCalForm({ nota: r.nota ?? '', comentario: r.comentario ?? '' });
    setCalOk(false);
    setRedaccionModal(r);
  };

  const abrirDuda = (d) => {
    setTextoRespuesta('');
    setRespOk(false);
    setDudaModal(d);
  };

  const responderDuda = async () => {
    if (!textoRespuesta.trim()) return;
    setRespondiendo(true);
    try {
      await axios.put(`${API_URL}/dudas/${dudaModal.id}/responder`, { respuesta: textoRespuesta.trim() });
      setRespOk(true);
      setStats(prev => ({
        ...prev,
        dudasPendientes: prev.dudasPendientes.filter(d => d.id !== dudaModal.id),
      }));
      setTimeout(() => setDudaModal(null), 1200);
    } catch {}
    finally { setRespondiendo(false); }
  };

  const calificar = async () => {
    if (calForm.nota === '' || isNaN(parseFloat(calForm.nota))) return;
    setCalGuardando(true);
    try {
      await axios.put(`${API_URL}/evaluaciones/redacciones/${redaccionModal.id}/calificar`, {
        nota: parseFloat(calForm.nota),
        comentario: calForm.comentario || null,
      });
      setCalOk(true);
      setStats(prev => ({
        ...prev,
        redaccionesPendientes: prev.redaccionesPendientes.filter(r => r.id !== redaccionModal.id),
      }));
      setTimeout(() => setRedaccionModal(null), 1200);
    } catch {}
    finally { setCalGuardando(false); }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: '10px',
    background: '#FFF0F5', border: '1px solid #FBCFE8',
    color: '#581C47', fontSize: '14px', boxSizing: 'border-box',
  };

  const card = { background: '#fff', border: '1px solid #FBCFE8', borderRadius: '16px', padding: '18px 20px', boxShadow: '0 2px 14px rgba(236,72,153,0.07)' };

  const numConectados = stats?.activosAhora?.length || 0;
  const numRedacciones = stats?.redaccionesPendientes?.length || 0;
  const numDudas = stats?.dudasPendientes?.length || 0;

  return (
    <div style={{ minHeight: '100vh', background: '#FFF0F5' }}>

      {/* Nav */}
      <nav style={{ background: '#fff', borderBottom: '1px solid #FBCFE8', padding: esMobile ? '0 14px' : '0 28px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 12px rgba(236,72,153,0.08)', position: 'sticky', top: 0, zIndex: 100, gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>🌸</span>
          {!esMobile && <span style={{ color: '#581C47', fontWeight: 700, fontSize: '15px' }}>Su Academia</span>}
          <span style={{ background: '#EDE9FE', color: '#7C3AED', border: '1px solid #DDD6FE', borderRadius: '6px', padding: '2px 8px', fontSize: '11px', fontWeight: 700 }}>PROFESOR</span>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => navigate('/aprendizaje')} style={{ background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            {esMobile ? '📊' : '📊 Progreso'}
          </button>
          <button onClick={() => { logout(); navigate('/'); }} style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: esMobile ? '7px 12px' : '8px 18px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            {esMobile ? <XMarkIcon style={{ width: 16, height: 16 }} /> : 'Salir'}
          </button>
        </div>
      </nav>

      <div style={{ padding: esMobile ? '20px 14px 60px' : '28px 24px', maxWidth: '900px', margin: '0 auto' }} className="fade-in">

        {/* Cabecera compacta */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '8px' }}>
          <div>
            <h1 style={{ color: '#581C47', fontSize: esMobile ? '19px' : '22px', fontWeight: 800, margin: 0 }}>Hola, {usuario?.nombre?.split(' ')[0]} 👋</h1>
            <p style={{ color: '#BE84A3', fontSize: '12px', margin: '3px 0 0' }}>{usuario?.email}</p>
          </div>
          {/* Badges de resumen */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '10px', padding: '6px 14px', textAlign: 'center' }}>
              <div style={{ color: '#059669', fontWeight: 800, fontSize: '18px', lineHeight: 1 }}>{numConectados}</div>
              <div style={{ color: '#34D399', fontSize: '10px', fontWeight: 600 }}>Online</div>
            </div>
            <div style={{ background: numRedacciones > 0 ? '#FFFBEB' : '#ECFDF5', border: `1px solid ${numRedacciones > 0 ? '#FDE68A' : '#A7F3D0'}`, borderRadius: '10px', padding: '6px 14px', textAlign: 'center' }}>
              <div style={{ color: numRedacciones > 0 ? '#D97706' : '#059669', fontWeight: 800, fontSize: '18px', lineHeight: 1 }}>{numRedacciones}</div>
              <div style={{ color: numRedacciones > 0 ? '#F59E0B' : '#34D399', fontSize: '10px', fontWeight: 600 }}>Redacc.</div>
            </div>
            <div style={{ background: numDudas > 0 ? '#EFF6FF' : '#ECFDF5', border: `1px solid ${numDudas > 0 ? '#BFDBFE' : '#A7F3D0'}`, borderRadius: '10px', padding: '6px 14px', textAlign: 'center' }}>
              <div style={{ color: numDudas > 0 ? '#1D4ED8' : '#059669', fontWeight: 800, fontSize: '18px', lineHeight: 1 }}>{numDudas}</div>
              <div style={{ color: numDudas > 0 ? '#3B82F6' : '#34D399', fontSize: '10px', fontWeight: 600 }}>Dudas</div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>

          {/* Conectados ahora */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#34D399', display: 'inline-block', boxShadow: '0 0 0 3px #34D39930' }} />
              <h2 style={{ color: '#581C47', fontSize: '14px', fontWeight: 700, margin: 0 }}>Conectados ahora</h2>
            </div>

            {cargando ? (
              <p style={{ color: '#BE84A3', fontSize: '13px' }}>Cargando...</p>
            ) : !stats?.activosAhora?.length ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: '28px', margin: '0 0 4px' }}>💤</p>
                <p style={{ color: '#BE84A3', fontSize: '12px', margin: 0 }}>Ningún alumno conectado</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto' }}>
                {stats.activosAhora.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#F6FFF9', borderRadius: '10px', border: '1px solid #D1FAE5' }}>
                    <div>
                      <p style={{ color: '#065F46', fontWeight: 700, fontSize: '13px', margin: 0 }}>{a.nombre}</p>
                      <p style={{ color: '#6EE7B7', fontSize: '11px', margin: '1px 0 0' }}>{tipoLabel(a.tipo)} · {a.nivel}</p>
                    </div>
                    <span style={{ fontSize: '10px', color: '#34D399', fontWeight: 700, background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '20px', padding: '2px 8px' }}>● Online</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Redacciones pendientes */}
          <div style={card}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <span style={{ fontSize: '16px' }}>✍️</span>
              <h2 style={{ color: '#581C47', fontSize: '14px', fontWeight: 700, margin: 0 }}>Redacciones por corregir</h2>
            </div>

            {cargando ? (
              <p style={{ color: '#BE84A3', fontSize: '13px' }}>Cargando...</p>
            ) : !stats?.redaccionesPendientes?.length ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: '28px', margin: '0 0 4px' }}>✅</p>
                <p style={{ color: '#BE84A3', fontSize: '12px', margin: 0 }}>Todo al día</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '260px', overflowY: 'auto' }}>
                {stats.redaccionesPendientes.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', background: '#FFFBEB', borderRadius: '10px', border: '1px solid #FDE68A' }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ color: '#78350F', fontWeight: 700, fontSize: '13px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.alumno_nombre}</p>
                      <p style={{ color: '#B45309', fontSize: '11px', margin: '1px 0 0' }}>{r.eval_titulo} · {tipoLabel(r.tipo)} {r.nivel}</p>
                    </div>
                    <button onClick={() => abrirRedaccion(r)}
                      style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      Corregir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Dudas pendientes */}
          <div style={{ ...card, gridColumn: esMobile ? 'auto' : '1 / -1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <span style={{ fontSize: '16px' }}>💬</span>
              <h2 style={{ color: '#581C47', fontSize: '14px', fontWeight: 700, margin: 0 }}>Dudas de alumnos sin responder</h2>
            </div>

            {cargando ? (
              <p style={{ color: '#BE84A3', fontSize: '13px' }}>Cargando...</p>
            ) : !stats?.dudasPendientes?.length ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <p style={{ fontSize: '28px', margin: '0 0 4px' }}>✅</p>
                <p style={{ color: '#BE84A3', fontSize: '12px', margin: 0 }}>No hay dudas pendientes</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '280px', overflowY: 'auto' }}>
                {stats.dudasPendientes.map(d => (
                  <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', padding: '10px 12px', background: '#EFF6FF', borderRadius: '10px', border: '1px solid #BFDBFE' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                        <p style={{ color: '#1E3A8A', fontWeight: 700, fontSize: '13px', margin: 0 }}>{d.alumno_nombre}</p>
                        <span style={{ color: '#60A5FA', fontSize: '10px' }}>{tipoLabel(d.tipo)} · {d.nivel}</span>
                        {d.evaluacion_titulo && <span style={{ color: '#93C5FD', fontSize: '10px' }}>— {d.evaluacion_titulo}</span>}
                      </div>
                      <p style={{ color: '#1D4ED8', fontSize: '12px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.pregunta}</p>
                    </div>
                    <button onClick={() => abrirDuda(d)}
                      style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)', border: 'none', color: '#fff', padding: '5px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                      Responder
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Modal corrección redacción */}
      {redaccionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(88,28,71,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target === e.currentTarget) setRedaccionModal(null); }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(88,28,71,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexShrink: 0 }}>
              <div>
                <h2 style={{ color: '#581C47', fontSize: '17px', fontWeight: 700, margin: 0 }}>✍️ Corregir redacción</h2>
                <p style={{ color: '#BE84A3', fontSize: '12px', marginTop: '3px' }}>{redaccionModal.alumno_nombre} · {redaccionModal.eval_titulo}</p>
              </div>
              <button onClick={() => setRedaccionModal(null)} style={{ background: 'none', border: 'none', color: '#BE84A3', cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', background: '#FFF9F5', border: '1px solid #FBCFE8', borderRadius: '12px', padding: '16px', marginBottom: '16px', marginTop: '12px' }}>
              <p style={{ color: '#BE84A3', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Texto del alumno</p>
              <p style={{ color: '#581C47', fontSize: '14px', lineHeight: '1.7', whiteSpace: 'pre-wrap', margin: 0 }}>{redaccionModal.texto_respuesta}</p>
            </div>

            {calOk ? (
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '12px', padding: '16px', textAlign: 'center', color: '#065F46', fontWeight: 600, flexShrink: 0 }}>
                ✅ Redacción calificada correctamente
              </div>
            ) : (
              <div style={{ flexShrink: 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '12px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ color: '#EC4899', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '5px' }}>Nota (0–10) *</label>
                    <input type="number" min="0" max="10" step="0.5"
                      value={calForm.nota}
                      onChange={e => setCalForm({ ...calForm, nota: e.target.value })}
                      style={{ ...inputStyle, textAlign: 'center' }}
                    />
                  </div>
                  <div>
                    <label style={{ color: '#EC4899', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '5px' }}>Comentario para el alumno</label>
                    <input type="text"
                      value={calForm.comentario}
                      onChange={e => setCalForm({ ...calForm, comentario: e.target.value })}
                      placeholder="Feedback opcional..."
                      style={inputStyle}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={calificar} disabled={calGuardando || calForm.nota === ''} style={{ flex: 1, padding: '12px', borderRadius: '50px', background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                    {calGuardando ? 'Guardando...' : 'Calificar y marcar como corregida'}
                  </button>
                  <button onClick={() => setRedaccionModal(null)} style={{ padding: '12px 18px', borderRadius: '50px', background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', fontSize: '14px', cursor: 'pointer' }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal responder duda */}
      {dudaModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(88,28,71,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={e => { if (e.target === e.currentTarget) setDudaModal(null); }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '28px', width: '100%', maxWidth: '560px', boxShadow: '0 24px 64px rgba(88,28,71,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <div>
                <h2 style={{ color: '#581C47', fontSize: '17px', fontWeight: 700, margin: 0 }}>💬 Responder duda</h2>
                <p style={{ color: '#BE84A3', fontSize: '12px', marginTop: '3px' }}>
                  {dudaModal.alumno_nombre} · {tipoLabel(dudaModal.tipo)} {dudaModal.nivel}
                  {dudaModal.evaluacion_titulo ? ` — ${dudaModal.evaluacion_titulo}` : ''}
                </p>
              </div>
              <button onClick={() => setDudaModal(null)} style={{ background: 'none', border: 'none', color: '#BE84A3', cursor: 'pointer' }}>
                <XMarkIcon style={{ width: 20, height: 20 }} />
              </button>
            </div>

            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '12px', padding: '14px 16px', margin: '14px 0' }}>
              <p style={{ color: '#60A5FA', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Pregunta del alumno</p>
              <p style={{ color: '#1E3A8A', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>{dudaModal.pregunta}</p>
            </div>

            {respOk ? (
              <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '12px', padding: '16px', textAlign: 'center', color: '#065F46', fontWeight: 600 }}>
                ✅ Duda respondida correctamente
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '14px' }}>
                  <label style={{ color: '#EC4899', fontSize: '12px', fontWeight: 600, display: 'block', marginBottom: '6px' }}>Tu respuesta *</label>
                  <textarea
                    value={textoRespuesta}
                    onChange={e => setTextoRespuesta(e.target.value)}
                    placeholder="Escribe aquí tu respuesta para el alumno..."
                    autoFocus
                    style={{ ...inputStyle, width: '100%', minHeight: '100px', resize: 'vertical', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={responderDuda} disabled={respondiendo || !textoRespuesta.trim()}
                    style={{ flex: 1, padding: '12px', borderRadius: '50px', background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
                    {respondiendo ? 'Enviando...' : 'Enviar respuesta'}
                  </button>
                  <button onClick={() => setDudaModal(null)} style={{ padding: '12px 18px', borderRadius: '50px', background: 'none', border: '1px solid #FBCFE8', color: '#BE84A3', fontSize: '14px', cursor: 'pointer' }}>
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
